/**
 * FormasRecebimentoPage Component
 * 
 * Página de listagem e gerenciamento de formas de recebimento.
 * Permite visualizar, criar, editar e excluir formas de recebimento que aparecerão no caixa.
 * 
 * Funcionalidades:
 * - Listagem de formas de recebimento
 * - Filtros por tipo e status
 * - Modal de criação/edição
 * - Controle de status ativo/inativo
 */

import { useState, useEffect, useCallback } from 'react'
import { Edit, Trash2, Plus, Search, ChevronDown, ChevronRight, ChevronDown as ChevronDownIcon } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import { paymentReceiptMethodService, financialAccountService } from '../../../../services/erpApi'
import DinheiroModal from './modals/DinheiroModal'
import DebitoModal from './modals/DebitoModal'
import CreditoVistaModal from './modals/CreditoVistaModal'
import CreditoParceladoModal from './modals/CreditoParceladoModal'
import PixQrcodeModal from './modals/PixQrcodeModal'
import PixCnpjModal from './modals/PixCnpjModal'
import CrediarioModal from './modals/CrediarioModal'
import './FormasRecebimentoPage.css'

function FormasRecebimentoPage() {
  const [formasRecebimento, setFormasRecebimento] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modalType, setModalType] = useState(null) // 'dinheiro', 'debito', 'credito-vista', 'credito-parcelado', 'pix-qrcode', 'pix-cnpj'
  const [editingForma, setEditingForma] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [errors, setErrors] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    active: ''
  })
  const [viewMode, setViewMode] = useState('hierarchy') // 'table' ou 'hierarchy'
  const [expandedTypes, setExpandedTypes] = useState({})
  const [expandedBrands, setExpandedBrands] = useState({})
  const [showCreateMenu, setShowCreateMenu] = useState(false)

  // Debug: log quando modalType muda
  useEffect(() => {
    console.log('modalType mudou para:', modalType)
  }, [modalType])

  const loadAccounts = useCallback(async () => {
    try {
      const response = await financialAccountService.getAll({ active: true })
      if (response.success) {
        setAccounts(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar contas:', error)
    }
  }, [])

  const loadFormasRecebimento = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Filtrar apenas os parâmetros que têm valor
      const cleanFilters = {}
      if (filters.search && filters.search.trim()) {
        cleanFilters.search = filters.search.trim()
      }
      if (filters.active !== '') {
        cleanFilters.active = filters.active
      }
      
      const response = await paymentReceiptMethodService.getAll(cleanFilters)
      if (response && response.success) {
        setFormasRecebimento(response.data || [])
      } else {
        const errorMessage = response?.message || 'Erro ao carregar formas de recebimento'
        setError(errorMessage)
        setFormasRecebimento([])
        console.error('Resposta inválida da API:', response)
      }
    } catch (error) {
      console.error('Erro ao carregar formas de recebimento:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao carregar formas de recebimento'
      setError(errorMessage)
      setFormasRecebimento([])
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadFormasRecebimento()
    loadAccounts()
  }, [loadFormasRecebimento, loadAccounts])

  const handleSubmit = async (dataToSend) => {
    console.log('handleSubmit chamado com:', dataToSend)
    setErrors({})

    try {
      let response
      if (editingForma) {
        console.log('Atualizando forma de recebimento:', editingForma.id)
        response = await paymentReceiptMethodService.update(editingForma.id, dataToSend)
      } else {
        console.log('Criando nova forma de recebimento')
        response = await paymentReceiptMethodService.create(dataToSend)
      }

      console.log('Resposta da API:', response)

      if (response.success) {
        console.log('Sucesso! Fechando modal e recarregando lista')
        setModalType(null)
        setEditingForma(null)
        setErrors({})
        loadFormasRecebimento()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar forma de recebimento' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar forma de recebimento:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar forma de recebimento'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleEdit = (forma) => {
    setEditingForma(forma)
    // Determinar o tipo de modal baseado no payment_type
    const paymentType = forma.payment_type
    if (paymentType === 'DINHEIRO') {
      setModalType('dinheiro')
    } else if (paymentType === 'DÉBITO') {
      setModalType('debito')
    } else if (paymentType === 'CRÉDITO_À_VISTA') {
      setModalType('credito-vista')
    } else if (paymentType === 'CRÉDITO_PARCELADO') {
      setModalType('credito-parcelado')
    } else if (paymentType === 'PIX_QRCODE') {
      setModalType('pix-qrcode')
    } else if (paymentType === 'PIX_CNPJ') {
      setModalType('pix-cnpj')
    } else if (paymentType === 'CREDIÁRIO' || paymentType === 'CREDIARIO') {
      setModalType('crediario')
    } else {
      // Fallback: tentar inferir do provider ou usar modal genérico
      if (forma.provider === 'DINHEIRO') {
        setModalType('dinheiro')
      } else {
        // Se não conseguir determinar, usar o modal de débito como padrão
        setModalType('debito')
      }
    }
  }

  const handleDelete = async (forma, force = false) => {
    const confirmMessage = force
      ? `ATENÇÃO: Você está forçando a exclusão da forma de recebimento "${forma.name}".\n\nIsso irá:\n- Remover referências em pagamentos de vendas\n- Remover referências em conciliações de cartão\n- DELETAR registros de conferência de caixa associados\n\nDeseja continuar?`
      : `Deseja realmente excluir a forma de recebimento "${forma.name}"?`
    
    if (window.confirm(confirmMessage)) {
      try {
        const response = await paymentReceiptMethodService.delete(forma.id, force)
        if (response.success) {
          loadFormasRecebimento()
          if (force) {
            alert('Forma de recebimento excluída com sucesso (exclusão forçada).\n\nTodas as referências foram removidas e os registros de conferência de caixa foram deletados.')
          }
        } else {
          // Se falhar e não for modo forçado, oferecer opção de forçar
          if (!force && response.message && response.message.includes('associados')) {
            const shouldForce = window.confirm(
              `${response.message}\n\nDeseja forçar a exclusão removendo todas as referências?`
            )
            if (shouldForce) {
              // Tentar novamente com force=true
              await handleDelete(forma, true)
            }
          } else {
            alert(response.message || 'Erro ao excluir forma de recebimento')
          }
        }
      } catch (error) {
        console.error('Erro ao excluir forma de recebimento:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao excluir forma de recebimento'
        
        // Se falhar e não for modo forçado, oferecer opção de forçar
        if (!force && errorMessage.includes('associados')) {
          const shouldForce = window.confirm(
            `${errorMessage}\n\nDeseja forçar a exclusão removendo todas as referências?`
          )
          if (shouldForce) {
            // Tentar novamente com force=true
            await handleDelete(forma, true)
          }
        } else {
          alert(errorMessage)
        }
      }
    }
  }

  const closeModal = () => {
    setModalType(null)
    setEditingForma(null)
    setErrors({})
  }

  // Agrupar formas de recebimento hierarquicamente: payment_type -> card_brand -> installments
  const groupHierarchically = (methods) => {
    const grouped = {}
    
    methods.forEach(method => {
      // Determinar payment_type (priorizar payment_type, depois provider)
      let paymentType = method.payment_type
      if (!paymentType) {
        if (method.provider === 'DINHEIRO') {
          paymentType = 'DINHEIRO'
        } else {
          paymentType = 'OUTROS'
        }
      }
      
      // Normalizar payment_type
      if (paymentType === 'CRÉDITO' && !method.installments) {
        paymentType = 'CRÉDITO_À_VISTA'
      } else if (paymentType === 'CRÉDITO' && method.installments) {
        paymentType = 'CRÉDITO_PARCELADO'
      }
      
      if (!grouped[paymentType]) {
        grouped[paymentType] = {}
      }
      
      // Agrupar PIX (não tem bandeira)
      if (paymentType === 'PIX_QRCODE' || paymentType === 'PIX_CNPJ') {
        if (!grouped[paymentType]['_sem_bandeira']) {
          grouped[paymentType]['_sem_bandeira'] = []
        }
        grouped[paymentType]['_sem_bandeira'].push(method)
      }
      // Se for método de cartão, agrupar por bandeira
      else if (method.card_brand) {
        const brand = method.card_brand
        if (!grouped[paymentType][brand]) {
          grouped[paymentType][brand] = []
        }
        grouped[paymentType][brand].push(method)
      } else {
        // Métodos sem bandeira (ex: Dinheiro)
        if (!grouped[paymentType]['_sem_bandeira']) {
          grouped[paymentType]['_sem_bandeira'] = []
        }
        grouped[paymentType]['_sem_bandeira'].push(method)
      }
    })
    
    // Ordenar dentro de cada grupo
    Object.keys(grouped).forEach(paymentType => {
      Object.keys(grouped[paymentType]).forEach(brand => {
        grouped[paymentType][brand].sort((a, b) => {
          const installmentsA = a.installments || 0
          const installmentsB = b.installments || 0
          return installmentsA - installmentsB
        })
      })
    })
    
    return grouped
  }
  
  const getPaymentTypeLabel = (type) => {
    const labels = {
      'DINHEIRO': '💰 Dinheiro',
      'PIX_QRCODE': '📱 PIX QRCode',
      'PIX_CNPJ': '📱 PIX CNPJ',
      'DÉBITO': '💳 Cartão de Débito',
      'CRÉDITO_À_VISTA': '💳 Crédito à Vista',
      'CRÉDITO_PARCELADO': '💳 Crédito Parcelado',
      'CREDIÁRIO': '📅 Crediário',
      'CREDIARIO': '📅 Crediário',
      'OUTROS': '📋 Outros'
    }
    return labels[type] || type
  }
  
  const getBrandLabel = (brand) => {
    const labels = {
      'MASTER': 'Mastercard',
      'VISA': 'Visa',
      'ELO': 'Elo',
      'AMEX': 'American Express',
      'HIPERCARD': 'Hipercard',
      'BANRISUL': 'Banrisul'
    }
    return labels[brand] || brand
  }

  const columns = [
    { key: 'name', label: 'Nome', sortable: true },
    {
      key: 'provider',
      label: 'Provedor',
      render: (value) => value || '-'
    },
    {
      key: 'payment_type',
      label: 'Tipo',
      render: (value) => value || '-'
    },
    {
      key: 'installments',
      label: 'Parcelas',
      render: (value) => value ? `${value}x` : 'À vista'
    },
    {
      key: 'card_brand',
      label: 'Bandeira',
      render: (value) => value || '-'
    },
    {
      key: 'account_name',
      label: 'Conta',
      render: (value, row) => row.account_name ? `${row.account_code} - ${row.account_name}` : '-'
    },
    {
      key: 'tax_rate',
      label: 'Taxa (%)',
      render: (value) => value ? `${parseFloat(value).toFixed(2)}%` : '-'
    },
    {
      key: 'active',
      label: 'Status',
      render: (value) => {
        return value ? (
          <span className="status-badge active">Ativa</span>
        ) : (
          <span className="status-badge inactive">Inativa</span>
        )
      }
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  // Componente de visualização hierárquica: payment_type -> card_brand -> installments
  const HierarchyView = ({ methods, loading, onEdit, onDelete, expandedTypes, setExpandedTypes, expandedBrands, setExpandedBrands }) => {
    if (loading) {
      return <div style={{ padding: '2rem', textAlign: 'center' }}>Carregando...</div>
    }

    if (methods.length === 0) {
      return <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Nenhuma forma de recebimento encontrada</div>
    }

    const grouped = groupHierarchically(methods)
    
    // Ordenar tipos: DINHEIRO primeiro, depois PIX, depois cartões, OUTROS
    const typeOrder = ['DINHEIRO', 'PIX_QRCODE', 'PIX_CNPJ', 'DÉBITO', 'CRÉDITO_À_VISTA', 'CRÉDITO_PARCELADO', 'OUTROS']
    const paymentTypes = Object.keys(grouped).sort((a, b) => {
      const indexA = typeOrder.indexOf(a)
      const indexB = typeOrder.indexOf(b)
      if (indexA !== -1 && indexB !== -1) return indexA - indexB
      if (indexA !== -1) return -1
      if (indexB !== -1) return 1
      return a.localeCompare(b)
    })

    const toggleType = (type) => {
      setExpandedTypes(prev => ({
        ...prev,
        [type]: !prev[type]
      }))
    }

    const toggleBrand = (type, brand) => {
      const key = `${type}_${brand}`
      setExpandedBrands(prev => ({
        ...prev,
        [key]: !prev[key]
      }))
    }

    return (
      <div className="hierarchy-view">
        {paymentTypes.map(paymentType => {
          const isTypeExpanded = expandedTypes[paymentType] !== false
          const brands = Object.keys(grouped[paymentType])
          const totalMethods = brands.reduce((sum, brand) => sum + grouped[paymentType][brand].length, 0)

          return (
            <div key={paymentType} className="hierarchy-type" style={{ marginBottom: '1rem' }}>
              <div 
                className="hierarchy-type-header"
                onClick={() => toggleType(paymentType)}
                style={{ 
                  padding: '1rem', 
                  background: '#f3f4f6', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                  fontSize: '1.125rem'
                }}
              >
                {isTypeExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <span>{getPaymentTypeLabel(paymentType)}</span>
                <span style={{ marginLeft: 'auto', fontSize: '0.875rem', fontWeight: 'normal', color: '#6b7280' }}>
                  {totalMethods} forma(s)
                </span>
              </div>

              {isTypeExpanded && (
                <div style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                  {brands.map(brand => {
                    const methodsInBrand = grouped[paymentType][brand]
                    const brandKey = `${paymentType}_${brand}`
                    const isBrandExpanded = expandedBrands[brandKey] !== false
                    const showBrandHeader = brand !== '_sem_bandeira' && paymentType !== 'DINHEIRO'

                    return (
                      <div key={brand} style={{ marginBottom: '0.5rem' }}>
                        {showBrandHeader && (
                          <div
                            onClick={() => toggleBrand(paymentType, brand)}
                            style={{
                              padding: '0.75rem 1rem',
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontWeight: 500,
                              fontSize: '1rem',
                              marginBottom: '0.5rem'
                            }}
                          >
                            {isBrandExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            <span>{getBrandLabel(brand)}</span>
                            <span style={{ marginLeft: 'auto', fontSize: '0.875rem', color: '#6b7280' }}>
                              {methodsInBrand.length} forma(s)
                            </span>
                          </div>
                        )}

                        {(isBrandExpanded || !showBrandHeader) && (
                          <div style={{ marginLeft: showBrandHeader ? '1rem' : '0' }}>
                            {methodsInBrand.map(method => (
                              <div
                                key={method.id}
                                className="hierarchy-method-item"
                                style={{
                                  padding: '0.75rem 1rem',
                                  background: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  marginBottom: '0.5rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                                    {method.name}
                                  </div>
                                  <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                    {method.installments && <span style={{ fontWeight: 500 }}>{method.installments}x</span>}
                                    {method.tax_rate !== undefined && method.tax_rate !== null && (
                                      <span>Taxa: {parseFloat(method.tax_rate).toFixed(2)}%</span>
                                    )}
                                    {method.account_name && <span>{method.account_code} - {method.account_name}</span>}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    className="btn-secondary"
                                    onClick={() => onEdit(method)}
                                    style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    className="btn-danger"
                                    onClick={() => onDelete(method)}
                                    style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="formas-recebimento-page">
      <div className="page-header">
        <div>
          <h1>Formas de Recebimento</h1>
          <p>Gerencie as formas de recebimento que aparecerão no caixa</p>
        </div>
        <div style={{ position: 'relative' }}>
          <button
            className="btn-primary"
            onClick={() => setShowCreateMenu(!showCreateMenu)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={18} />
            Nova Forma de Recebimento
            <ChevronDownIcon size={16} style={{ marginLeft: '0.25rem' }} />
          </button>
          
          {showCreateMenu && (
            <>
              <div 
                style={{ 
                  position: 'fixed', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  zIndex: 999,
                  background: 'transparent'
                }}
                onClick={() => setShowCreateMenu(false)}
              />
              <div 
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.5rem',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 1001,
                  minWidth: '220px',
                  overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="btn-menu-item"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Abrindo modal Dinheiro')
                    setShowCreateMenu(false)
                    setEditingForma(null)
                    setErrors({})
                    setModalType('dinheiro')
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  💰 Dinheiro
                </button>
                <button
                  className="btn-menu-item"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Abrindo modal Débito')
                    setShowCreateMenu(false)
                    setEditingForma(null)
                    setErrors({})
                    setModalType('debito')
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    borderTop: '1px solid #e5e7eb'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  💳 Cartão de Débito
                </button>
                <button
                  className="btn-menu-item"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Abrindo modal Crédito à Vista')
                    setShowCreateMenu(false)
                    setEditingForma(null)
                    setErrors({})
                    setModalType('credito-vista')
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    borderTop: '1px solid #e5e7eb'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  💳 Crédito à Vista
                </button>
                <button
                  className="btn-menu-item"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Abrindo modal Crédito Parcelado')
                    setShowCreateMenu(false)
                    setEditingForma(null)
                    setErrors({})
                    setModalType('credito-parcelado')
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    borderTop: '1px solid #e5e7eb'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  💳 Crédito Parcelado
                </button>
                <button
                  className="btn-menu-item"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setShowCreateMenu(false)
                    setEditingForma(null)
                    setErrors({})
                    setModalType('crediario')
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    borderTop: '1px solid #e5e7eb'
                  }}
                  onMouseEnter={(e) => { e.target.style.background = '#f3f4f6' }}
                  onMouseLeave={(e) => { e.target.style.background = 'transparent' }}
                >
                  📅 Crediário
                </button>
                <button
                  className="btn-menu-item"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Abrindo modal PIX QRCode')
                    setShowCreateMenu(false)
                    setEditingForma(null)
                    setErrors({})
                    setModalType('pix-qrcode')
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    borderTop: '1px solid #e5e7eb'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  📱 PIX QRCode
                </button>
                <button
                  className="btn-menu-item"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Abrindo modal PIX CNPJ')
                    setShowCreateMenu(false)
                    setEditingForma(null)
                    setErrors({})
                    setModalType('pix-cnpj')
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    borderTop: '1px solid #e5e7eb'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  📱 PIX CNPJ
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="page-filters">
        <div className="filters-row">
          <div className="search-input-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar formas de recebimento..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="search-input"
            />
          </div>
          <select
            value={filters.active}
            onChange={(e) => setFilters({ ...filters, active: e.target.value })}
            className="filter-select"
          >
            <option value="">Todos os status</option>
            <option value="true">Ativas</option>
            <option value="false">Inativas</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{ 
          background: '#fee2e2', 
          color: '#991b1b', 
          padding: '1rem', 
          borderRadius: '8px', 
          marginBottom: '1rem' 
        }}>
          <strong>Erro:</strong> {error}
          <button 
            onClick={loadFormasRecebimento}
            style={{ 
              marginLeft: '1rem', 
              padding: '0.5rem 1rem', 
              background: '#991b1b', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Toggle de visualização */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
        <button
          className={viewMode === 'hierarchy' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setViewMode('hierarchy')}
          style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
        >
          Visualização Hierárquica
        </button>
        <button
          className={viewMode === 'table' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setViewMode('table')}
          style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
        >
          Tabela
        </button>
      </div>

      {viewMode === 'hierarchy' ? (
        <HierarchyView
          methods={formasRecebimento}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          expandedTypes={expandedTypes}
          setExpandedTypes={setExpandedTypes}
          expandedBrands={expandedBrands}
          setExpandedBrands={setExpandedBrands}
        />
      ) : (
        <DataTable
          columns={columns}
          data={formasRecebimento}
          loading={loading}
          actions={actions}
          emptyMessage="Nenhuma forma de recebimento encontrada"
        />
      )}

      {/* Modais específicos por tipo */}
      {modalType && (
        <>
          <DinheiroModal
            isOpen={modalType === 'dinheiro'}
            onClose={() => {
              console.log('Fechando modal Dinheiro')
              closeModal()
            }}
            onSubmit={handleSubmit}
            editingForma={editingForma}
            accounts={accounts}
            errors={errors}
          />

          <DebitoModal
            isOpen={modalType === 'debito'}
            onClose={() => {
              console.log('Fechando modal Débito')
              closeModal()
            }}
            onSubmit={handleSubmit}
            editingForma={editingForma}
            accounts={accounts}
            errors={errors}
          />

          <CreditoVistaModal
            isOpen={modalType === 'credito-vista'}
            onClose={() => {
              console.log('Fechando modal Crédito à Vista')
              closeModal()
            }}
            onSubmit={handleSubmit}
            editingForma={editingForma}
            accounts={accounts}
            errors={errors}
          />

          <CreditoParceladoModal
            isOpen={modalType === 'credito-parcelado'}
            onClose={() => {
              console.log('Fechando modal Crédito Parcelado')
              closeModal()
            }}
            onSubmit={handleSubmit}
            editingForma={editingForma}
            accounts={accounts}
            errors={errors}
          />

          <PixQrcodeModal
            isOpen={modalType === 'pix-qrcode'}
            onClose={() => {
              console.log('Fechando modal PIX QRCode')
              closeModal()
            }}
            onSubmit={handleSubmit}
            editingForma={editingForma}
            accounts={accounts}
            errors={errors}
          />

          <PixCnpjModal
            isOpen={modalType === 'pix-cnpj'}
            onClose={() => {
              console.log('Fechando modal PIX CNPJ')
              closeModal()
            }}
            onSubmit={handleSubmit}
            editingForma={editingForma}
            accounts={accounts}
            errors={errors}
          />

          <CrediarioModal
            isOpen={modalType === 'crediario'}
            onClose={closeModal}
            onSubmit={handleSubmit}
            editingForma={editingForma}
            errors={errors}
          />
        </>
      )}
    </div>
  )
}

export default FormasRecebimentoPage

